import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import { SwarmType } from '@/graphql/types';
import { highestVtopQuery } from '../../../../../../graph/queries';
import { fixFloat } from '../../../../../../graph/data-types';
import { DATA_TYPE } from '../../../../../../graph/enums';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { buildRequiredMeasuringPointWindowInput } from '../measuringPointInput';
import { buildGroupedRowsFromColumns } from '../tabular';
import {
    RuntimeDataService,
    type RuntimeDataServiceRequest,
    type RuntimeResultLinkDescriptorTabular,
    type RuntimeTabularColumnDescriptor,
} from '../types';
import {
    isRecord,
    normalizeArrayEntries,
    normalizeNullableFiniteNumber,
    normalizeOrderedTimeWindow,
    normalizeRequiredFiniteNumber,
    normalizeRequiredPositiveIntegerId,
    toPositiveIntegerIdNumber,
} from '../validationPrimitives';

const HIGHEST_READING_AXES = ['x', 'y', 'z', 'vector'] as const;

type HighestReadingAxis = (typeof HIGHEST_READING_AXES)[number];
type HighestReadingEntry =
    RuntimeDataServiceOutput<'measuring_point.highest_readings'>['entries'][number];

interface HighestReadingsQueryVariables {
    measuringPointId: number;
    startTime: number;
    endTime: number;
}

// Shaped as the wire allows; `normalizeHighestVtopResponse` narrows it before anything reads it.
interface HighestReadingsQueryData {
    highestVtopEntries?: {
        entries?: unknown[] | null;
        vectorEnabled?: boolean | null;
    } | null;
}

function toMeasuringPointIdNumber(measuringPointId: string): number {
    return toPositiveIntegerIdNumber(
        measuringPointId,
        'measuring_point.highest_readings.measuringPointId'
    );
}

function isHighestReadingAxis(value: unknown): value is HighestReadingAxis {
    return HIGHEST_READING_AXES.some((axis) => axis === value);
}

function normalizeHighestReadingEntry(value: unknown, pathLabel: string): HighestReadingEntry {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    if (!isHighestReadingAxis(value.axis)) {
        throw new Error(`Expected "${pathLabel}.axis" to be a supported axis.`);
    }

    return {
        timestamp: normalizeRequiredFiniteNumber(value.timestamp, `${pathLabel}.timestamp`),
        axis: value.axis,
        fdom: normalizeNullableFiniteNumber(value.fdom, `${pathLabel}.fdom`),
        vtop: normalizeNullableFiniteNumber(value.vtop, `${pathLabel}.vtop`),
        percentage: normalizeNullableFiniteNumber(value.percentage, `${pathLabel}.percentage`),
    };
}

function validateHighestReadingsInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point.highest_readings'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point.highest_readings" input to be an object.');
    }

    const { startTime, endTime } = normalizeOrderedTimeWindow(
        input.startTime,
        input.endTime,
        'measuring_point.highest_readings'
    );

    return {
        measuringPointId: normalizeRequiredPositiveIntegerId(
            input.measuringPointId,
            'measuring_point.highest_readings.measuringPointId'
        ),
        startTime,
        endTime,
    };
}

function validateHighestReadingsResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.highest_readings'> {
    if (!isRecord(result)) {
        throw new Error('Expected "measuring_point.highest_readings" result to be an object.');
    }

    return {
        entries: normalizeArrayEntries(
            result.entries,
            'measuring_point.highest_readings.entries',
            normalizeHighestReadingEntry
        ),
    };
}

function parseHighestReadingsTableRows(value: unknown): readonly HighestReadingEntry[] {
    return validateHighestReadingsResult({ entries: value }).entries;
}

/**
 * The rows of one response entry. `flattenAxisRows` emits the axes in a fixed order starting at
 * `x`, so an `x` row opens a new entry. Grouping on the timestamp instead would merge two
 * distinct readings that share one, which the backend does return: `sbr_gather_stats` sorts
 * `get_highest_values` by magnitude and deliberately keeps that order rather than by time.
 */
function groupRowsByEntry(rows: readonly HighestReadingEntry[]): HighestReadingEntry[][] {
    const [firstAxis] = HIGHEST_READING_AXES;

    return rows.reduce<HighestReadingEntry[][]>((groups, row, index) => {
        if (row.axis === firstAxis) {
            groups.push([row]);
            return groups;
        }

        const current = groups[groups.length - 1];
        if (!current) {
            throw new Error(
                `Expected "measuring_point.highest_readings.entries[${index}]" to follow an "${firstAxis}" axis row.`
            );
        }

        current.push(row);
        return groups;
    }, []);
}

/**
 * Turn each per-timestamp response entry into one row per axis. The vector axis is included
 * only when the response enables it, which is why this cannot live in `parseTableRows`: that
 * sees the entry list alone, not the response-level flag.
 */
function flattenAxisRows(entries: readonly unknown[], vectorEnabled: boolean): unknown[] {
    const axes = vectorEnabled
        ? HIGHEST_READING_AXES
        : HIGHEST_READING_AXES.filter((axis) => axis !== 'vector');

    return entries.flatMap((entry, index) => {
        if (!isRecord(entry)) {
            throw new Error(
                `Expected "measuring_point.highest_readings.entries[${index}]" to be an object.`
            );
        }

        const { timestamp, fdom, vtop, percentage } = entry;
        if (!isRecord(fdom) || !isRecord(vtop) || !isRecord(percentage)) {
            throw new Error(
                `Expected per-axis fdom, vtop and percentage on "measuring_point.highest_readings.entries[${index}]".`
            );
        }

        return axes.map((axis) => ({
            timestamp,
            axis,
            fdom: fdom[axis],
            vtop: vtop[axis],
            percentage: percentage[axis],
        }));
    });
}

/**
 * `resolve_highest_vtop_entries` has two return paths and both set `entries` to a list and
 * `vector_enabled` to a boolean, so anything else is a broken response rather than a state to
 * absorb. Defaulting `vectorEnabled` would silently drop the vector column.
 */
function normalizeHighestVtopResponse(data: HighestReadingsQueryData): {
    entries: readonly unknown[];
    vectorEnabled: boolean;
} {
    const response = data.highestVtopEntries;
    if (!response) {
        throw new Error('Missing highest readings query result for runtime data services.');
    }

    if (!Array.isArray(response.entries)) {
        throw new Error('Expected "highestVtopEntries.entries" to be an array.');
    }

    if (typeof response.vectorEnabled !== 'boolean') {
        throw new Error('Expected "highestVtopEntries.vectorEnabled" to be a boolean.');
    }

    return { entries: response.entries, vectorEnabled: response.vectorEnabled };
}

function createHighestReadingColumns() {
    return [
        {
            kind: 'timestamp',
            key: 'timestamp',
            label: gettext('Date/Time'),
            defaultAlignment: 'left',
            resolveCell: (entry: HighestReadingEntry) => entry.timestamp,
        },
        {
            kind: 'text',
            key: 'axis',
            label: gettext('AXIS'),
            defaultAlignment: 'left',
            resolveCell: (entry: HighestReadingEntry) => entry.axis,
        },
        {
            kind: 'measurement',
            key: 'fdom',
            dataType: DATA_TYPE.FDOM,
            defaultAlignment: 'right',
            resolveCell: (entry: HighestReadingEntry) => entry.fdom,
        },
        {
            kind: 'measurement',
            key: 'vtop',
            dataType: DATA_TYPE.VTOP,
            defaultAlignment: 'right',
            resolveCell: (entry: HighestReadingEntry) => entry.vtop,
        },
        {
            // The percentage is not an Fdom reading; it borrows Fdom's single decimal only, as
            // the legacy table does. Its own name already says what the number is, so it takes
            // no unit and stays out of the measurement kind.
            kind: 'text',
            key: 'percentage',
            label: gettext('READING_PERCENTAGE'),
            defaultAlignment: 'right',
            resolveCell: (entry: HighestReadingEntry) =>
                entry.percentage === null ? null : fixFloat(DATA_TYPE.FDOM, entry.percentage),
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<HighestReadingEntry>[];
}

export class HighestReadingsRuntimeDataService extends RuntimeDataService<'measuring_point.highest_readings'> {
    static readonly serviceId = 'measuring_point.highest_readings' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_HIGHEST_READINGS_LABEL'),
            description: gettext('DATA_SERVICE_HIGHEST_READINGS_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        const columns = createHighestReadingColumns();

        return [
            {
                key: 'entries',
                label: gettext('DATA_SERVICE_HIGHEST_READINGS_ENTRIES_LABEL'),
                description: gettext('DATA_SERVICE_HIGHEST_READINGS_ENTRIES_DESCRIPTION'),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: [SwarmType.Vibration],
                path: 'entries',
                valueKind: 'tabular',
                // The row count is bounded server side by the measuring point's
                // `highest_n_records` setting, so there is no row limit to configure here.
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                columns,
                parseTableRows: parseHighestReadingsTableRows,
                buildTableShape: (settings, linkValue, _context, options) => ({
                    header: settings.header,
                    rows: buildGroupedRowsFromColumns(
                        groupRowsByEntry(parseHighestReadingsTableRows(linkValue)),
                        columns,
                        'timestamp',
                        options
                    ),
                    columnAlignments: settings.columnAlignments,
                    columnWidths: settings.columnWidths,
                }),
                buildInput: (args) =>
                    buildRequiredMeasuringPointWindowInput(args, 'highest readings'),
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptorTabular<
            'measuring_point.highest_readings',
            HighestReadingEntry
        >[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateHighestReadingsInput;

    validateResult = validateHighestReadingsResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'measuring_point.highest_readings'>) =>
        from(
            this.client.query<HighestReadingsQueryData, HighestReadingsQueryVariables>({
                query: highestVtopQuery,
                variables: {
                    measuringPointId: toMeasuringPointIdNumber(input.measuringPointId),
                    startTime: input.startTime,
                    endTime: input.endTime,
                },
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                const { entries, vectorEnabled } = normalizeHighestVtopResponse(data);

                return validateHighestReadingsResult({
                    entries: flattenAxisRows(entries, vectorEnabled),
                });
            })
        );
}

export function createHighestReadingsService(
    client: ApolloClient<object>
): HighestReadingsRuntimeDataService {
    return new HighestReadingsRuntimeDataService(client);
}
